import { useEffect, useState } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { v4 as uuidv4 } from 'uuid';

import { JWT_TOKEN } from '@constants';
import { localStorage } from '@utils';

enum MessageStatus {
  SENDING = 'sending',
  SENT = 'sent',
  ERROR = 'error',
}

export interface MessageType {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  status?: MessageStatus;
}

export interface ChatSettings {
  maxIterations?: number;
  mode?: 'hybrid' | 'global' | 'local' | 'mix';
}

type UseCopilotWebSocketProps = {
  url: string;
  shouldConnect?: boolean;
  settings?: ChatSettings;
};

export const useCopilotWebSocket = (props: UseCopilotWebSocketProps) => {
  const { url, settings, shouldConnect = true } = props;

  const [socketKey, setSocketKey] = useState(0);
  const [messages, setMessages] = useState<MessageType[]>([]);

  const [isWaitingResponse, setIsWaitingResponse] = useState(false);

  const token = localStorage.get<string>(JWT_TOKEN);

  const { readyState, lastJsonMessage, sendJsonMessage } = useWebSocket(
    url,
    {
      reconnectInterval: 15000,
      queryParams: { key: socketKey.toString() },
      protocols: ['copilot.apolo.us', `bearer.apolo.us-${token}`],
    },
    shouldConnect || isWaitingResponse,
  );

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const msg = lastJsonMessage as any;

    // eslint-disable-next-line default-case
    switch (msg.type) {
      case 'progress': {
        setMessages((prev) =>
          prev.map((message) =>
            message.status === MessageStatus.SENDING
              ? { ...message, status: MessageStatus.SENT }
              : message,
          ),
        );
        setIsWaitingResponse(true);
        break;
      }

      case 'response': {
        const assistantMessage: MessageType = {
          id: uuidv4(),
          role: 'assistant',
          content: msg.data.response,
          status: MessageStatus.SENT,
        };

        setMessages((prev) => [
          ...prev.map((message) =>
            message.status === MessageStatus.SENDING
              ? { ...message, status: MessageStatus.SENT }
              : message,
          ),
          assistantMessage,
        ]);

        setIsWaitingResponse(false);
        break;
      }

      case 'error': {
        const errorMessage: MessageType = {
          id: uuidv4(),
          role: 'assistant',
          content: msg.data.error,
          status: MessageStatus.ERROR,
        };

        setMessages((prev) => [
          ...prev.map((message) =>
            message.status === MessageStatus.SENDING
              ? { ...message, status: MessageStatus.ERROR }
              : message,
          ),
          errorMessage,
        ]);

        setIsWaitingResponse(false);
        break;
      }
    }
  }, [lastJsonMessage]);

  const clearHistory = () => {
    setMessages([]);
  };

  const sendMessage = (query: string) => {
    if (readyState !== ReadyState.OPEN) {
      return;
    }

    const userMessage: MessageType = {
      id: uuidv4(),
      role: 'user',
      content: query,
      status: MessageStatus.SENDING,
    };

    const payload = {
      type: 'query',
      data: {
        query,
        conversation_history: messages.map(({ role, content }) => ({
          role,
          content,
        })),
        ...(settings?.maxIterations && {
          max_iterations: settings.maxIterations,
        }),
        ...(settings?.mode && { mode: settings.mode }),
      },
    };

    sendJsonMessage(payload);
    setMessages((prev) => [...prev, userMessage]);
  };

  const resendMessage = (message: MessageType) => {
    if (readyState !== ReadyState.OPEN) {
      return;
    }

    const payload = {
      type: 'query',
      data: {
        query: message.content,
        conversation_history: messages
          .filter(({ id }) => id !== message.id)
          .map(({ role, content }) => ({
            role,
            content,
          })),
      },
    };

    sendJsonMessage(payload);
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === message.id ? { ...msg, status: MessageStatus.SENDING } : msg,
      ),
    );
  };

  const reconnect = () => {
    setSocketKey((prev) => prev + 1);
  };

  useEffect(() => {
    if (readyState !== ReadyState.OPEN) {
      setIsWaitingResponse(false);
    }
  }, [readyState]);

  return {
    messages,
    readyState,
    isWaitingResponse,
    reconnect,
    sendMessage,
    clearHistory,
    resendMessage,
  };
};
